<script lang="ts" setup>
import Button5 from '@/components/Button/Button5.vue'
import { useOptionStore, useGlobalStore } from '@/stores'
import subscribApi from '@/utils/api/subscribe'
import { useReCaptcha } from 'vue-recaptcha-v3'
import { useReCaptchaV3 } from '@/utils/helper'

const useReCaptchaInstance = useReCaptcha()!
const optionStore = useOptionStore()
const globalStore = useGlobalStore()
const topics = computed(() => optionStore.subscribeTopics)
const popupShow = ref(false)

const form = reactive({
  email: '',
  topics: [],
  google_recaptcha_token: '',
})
const submitBtnText: Ref<string> = ref('訂閱')
const submitBtnStatus: Ref<string> = ref('')
const handleSubmit = async () => {
  if (isEmpty(form.email) || !isEmail(form.email)) {
    globalStore.setDialogVisible({
      visible: true,
      title: '提示',
      message: '請輸入正確的電子信箱',
    })
    return
  }
  // 避免double click
  if (submitBtnStatus.value === '') {
    const token = await useReCaptchaV3(useReCaptchaInstance, 'inhome')
    form.google_recaptcha_token = token || ''
    submitBtnText.value = '處理中...'
    submitBtnStatus.value = 'loading'

    const params = {
      email: form.email,
      topics: form.topics,
      google_recaptcha_token: form.google_recaptcha_token,
    }

    subscribApi.submitSubscribeTopic(params).then((res: any) => {
      setTimeout(() => {
        submitBtnText.value = '訂閱'
        submitBtnStatus.value = ''
        globalStore.setDialogVisible({
          visible: true,
          title: '提示',
          message: `成功訂閱電子報。`,
        })
        form.email = ''
        form.topics = []
        form.google_recaptcha_token = ''
        popupShow.value = false
      }, 300)
    }).catch((err: any) => {
      submitBtnText.value = '送出'
      submitBtnStatus.value = ''
      globalStore.setDialogVisible({
        visible: true,
        title: '提示',
        message: `訂閱失敗。(${err.data.message})`,
      })
      console.log('訂閱失敗電子報送出失敗：', err)
    })
  }
}

const next = () => {
  if (isEmpty(form.email) || !isEmail(form.email)) {
    globalStore.setDialogVisible({
      visible: true,
      title: '提示',
      message: '請輸入正確的電子信箱',
    })
    return
  }
  popupShow.value = true
}

onMounted(() => {
  nextTick(() => {
    optionStore.fetchSubscribeTopics()
  })
})
</script>

<template>
  <section class="newsletter">
    <div class="newsletter__inner" v-show="!popupShow">
      <div class="newsletter__mainTitle">subscription</div>
      <div class="newsletter__content">
        <div class="newsletter__title">電子報訂閱</div>
        <div class="newsletter__text">立即訂閱電子報，享有第一手優惠資訊</div>
        <div class="newsletter__inputBox">
          <input type="text" v-model="form.email" placeholder="電子信箱" class="newsletter__input" />
          <Button5 linkTitle="訂閱" @click="next" type="button" />
        </div>
      </div>
    </div>
    <transition>
      <div class="newsletter__popup" v-show="popupShow">
        <div class="newsletter__popup__inner">
          <div class="newsletter__popup__title">對哪個主題比較有興趣？</div>
          <el-checkbox-group class="newsletter__checkGroup" v-model="form.topics">
            <el-checkbox v-for="item in topics" :key="item.id" :label="item.id"
              class="h-checkbox newsletter__checkItem">{{
      item.subject }}</el-checkbox>
          </el-checkbox-group>
          <div class="newsletter__popup__button">
            <Button5 linkTitle="送出" @click="handleSubmit" type="button" />
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<style lang="scss" scoped>
.newsletter {
  @include grid;
  padding-top: toRem(55);
  padding-bottom: toRem(110);
  background-color: $color-white-2;

  @include max-media(1180) {
    @include grid(4, 24);
  }

  @include max-media(767) {
    padding-top: toRem(56);
    padding-bottom: toRem(56);
  }

  &__inner {
    grid-column: 4 / span 6;

    @include max-media(1180) {
      grid-column: 1 / span 4;
    }
  }

  &__mainTitle {
    margin-bottom: toRem(53);
    font-family: $font-TextaAlt;
    font-size: toRem(132);
    color: $color-brand-4_1;
    font-weight: 500;
    line-height: 0.8;
    text-align: center;

    @include max-media(767) {
      font-size: toRem(60);
      margin-bottom: toRem(12);
    }
  }

  &__content {
    width: 100%;
    max-width: toRem(445);
    margin-left: auto;
    margin-right: auto;

    @include max-media(767) {
      padding-left: toRem(25);
      padding-right: toRem(25);
    }
  }

  &__title {
    margin-bottom: toRem(12);
    font-size: toRem(22);
    font-weight: 500;
    line-height: 1.3;

    @include max-media(767) {
      text-align: center;
      font-size: toRem(20);
    }
  }

  &__text {
    margin-bottom: toRem(12);
    font-size: toRem(16);
    color: $color-gray-1;
    line-height: 1.45;

    @include max-media(767) {
      text-align: center;
      font-size: toRem(14);
    }
  }

  &__inputBox {
    display: flex;
    width: 100%;
    height: toRem(47);
    background-color: $color-white-1;
    border-radius: toRem(50);
    overflow: hidden;
  }

  &__input {
    flex: 1;
    height: toRem(47);
    padding: toRem(16);
    border: none;
    font-size: toRem(16);

    &::placeholder {
      color: $color-gray-1;
    }

    &:focus {
      outline: none;
    }
  }

  &__popup {
    grid-column: 1 / span 12;

    :deep(.el-checkbox__label) {
      font-size: toRem(14);
    }

    &__inner {
      width: 100%;
      max-width: toRem(620);
      margin-left: auto;
      margin-right: auto;
      background-color: #fbecd0;
      border-radius: toRem(40);
      padding: toRem(56);
    }

    &__title {
      font-weight: 500;
      font-size: toRem(22);
      text-align: center;
      line-height: 1.3;
      margin-bottom: toRem(24);
    }

    &__button {
      display: flex;
      justify-content: center;
    }
  }

  &__checkGroup {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
  }

  &__checkItem {
    width: 25%;
    padding-right: toRem(12);
    margin-right: 0;
    margin-bottom: toRem(24);
  }
}
</style>
